<script setup>
    import { ref, watch } from "vue";
    import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
    import { Link, useForm, usePage } from "@inertiajs/vue3";

    const props = defineProps({
        inStockAlertSuccess: {
            type: Boolean,
            default: false,
        },
        auth: {
            type: Object,
            default: null,
        },
        selectedVariandId: {
            type: Number,
            default: null,
        },
        product: {
            type: Object,
            default: null,
        },
    });

    const page = usePage();
    let showInStockAlertSuccessPopup = ref(page.props.inStockAlertSuccess ?? false);
    let isStockAlertOpen = ref(false);
    const form = useForm({
        product_id: props.product.id,
        email: null,
    });

    const closeInStockForm = () => {
        showInStockAlertSuccessPopup.value = false;
        page.props.inStockAlertSuccess = false;
    };

    const closeStockAlertModal = () => {
        isStockAlertOpen.value = false;
    };

    const clearInStockFormError = () => {
        if (page.props.errors) {
            page.props.errors.inStockAlert = null;
        }
    };

    const openStockAlertModal = () => {
        isStockAlertOpen.value = true;
    };

    const submitForm = () => {
        form.post(route("inStockAlert"), {
            preserveScroll: true,
            onSuccess: () => {
                showInStockAlertSuccessPopup.value = true;
                isStockAlertOpen.value = false;
                form.reset();
            },
            only: ["errors", "inStockAlertSuccess"],
        });
    };

    watch(
        () => page.props.inStockAlertSuccess,
        function (value) {
            showInStockAlertSuccessPopup.value = value;
        }
    );
</script>

<template>
    <div class="mt-0">
        <!-- Çalışır hale geldikten sonra div dışına alınabilir. -->
        <Link
            v-if="auth.isUserLoggedIn"
            :href="route('inStockAlert')"
            :data="{
                product_id: product.id,
                email: auth.user.email,
                user_id: auth.user.id,
            }"
            :only="['errors', 'inStockAlertSuccess']"
            method="post"
            as="button"
            class="w-full block text-white rounded-full py-3 px-4 self-center font-semibold text-lg leading-none text-center bg-minigreen opacity-90 hover:opacity-100"
        >
            Stoğa Gelince Haber Ver
        </Link>

        <div v-if="!auth.isUserLoggedIn" @click="openStockAlertModal" class="w-full block text-white rounded-full py-3 px-4 self-center font-semibold text-lg leading-none text-center bg-minigreen opacity-90 hover:opacity-100 cursor-pointer">Stoğa Gelince Haber Ver</div>
    </div>

    <TransitionRoot appear :show="isStockAlertOpen" as="template">
        <Dialog as="div" @close="closeStockAlertModal" class="relative z-10">
            <div class="fixed inset-0 overflow-y-auto bg-black/20">
                <div class="flex min-h-full items-center justify-center p-4 text-center">
                    <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0 scale-95" enter-to="opacity-100 scale-100" leave="duration-200 ease-in" leave-from="opacity-100 scale-100" leave-to="opacity-0 scale-95">
                        <DialogPanel class="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white px-5 py-5 z-70 text-left align-middle shadow-xl transition-all flex flex-col justify-center items-center">
                            <div class="flex flex-col w-full">
                                <div class="text-black text-base md:text-2xl lg:text-2xl text-center mt-4 mx-auto">Stoğa Gelince Haber Ver.</div>
                                <form @submit.prevent="submitForm">
                                    <div class="text-center mb-6 mt-6 flex flex-col mts:flex-row justify-center w-full">
                                        <input
                                            v-model="form.email"
                                            @input="clearInStockFormError"
                                            name="email"
                                            type="email"
                                            class="rounded-2lg mts:rounded-none mts:rounded-l-2lg mts:w-8/12 leading-[70px] h-10 placeholder:text-sm md:placeholder:text-lg placeholder:text-placeholdergray py-6 md:px-6 text-base focus:outline-none"
                                            placeholder="E-Posta Adresin"
                                            required="required"
                                        />
                                        <input type="hidden" name="product_id" v-model="form.product_id" />
                                        <button type="submit" :disabled="form.processing" class="bg-kbgreen disabled:bg-kbgreen/20 disabled:cursor-wait text-base font-santralextrabold text-white px-8 py-3 mt-4 mts:mt-0 rounded-2lg mts:rounded-none mts:rounded-r-2lg border-0 focus:outline-none whitespace-nowrap">Tamamla</button>
                                    </div>
                                    <div class="text-center mb-6 mt-6 flex flex-col mts:flex-row justify-center w-full" v-if="page.props?.errors?.inStockAlert">
                                        <div class="text-red-500 text-sm mts:text-base">{{ Object.values(page.props.errors.inStockAlert)[0] }}</div>
                                    </div>
                                </form>
                            </div>
                            <div class="absolute top-2 right-5 cursor-pointer p-2" @click="closeStockAlertModal">x</div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

    <!-- Start of the result popup -->
    <TransitionRoot appear :show="showInStockAlertSuccessPopup" as="template">
        <Dialog as="div" @close="closeInStockForm" class="relative z-50">
            <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100" leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-black bg-opacity-25" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center">
                    <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0 scale-95" enter-to="opacity-100 scale-100" leave="duration-200 ease-in" leave-from="opacity-100 scale-100" leave-to="opacity-0 scale-95">
                        <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white px-5 py-5 z-70 text-left align-middle shadow-xl transition-all flex flex-col justify-center items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 62.051 62.051">
                                <g id="Group_4884" data-name="Group 4884" transform="translate(-73.036 -250.295)">
                                    <circle id="Ellipse_199" data-name="Ellipse 199" cx="31.025" cy="31.025" r="31.025" transform="translate(73.036 250.295)" fill="#ff6c37" />
                                    <path id="Path_3299" data-name="Path 3299" d="M351.649,476.232a3.45,3.45,0,0,1-2.761-1.381l-8.464-11.286a3.451,3.451,0,0,1,5.522-4.141l5.538,7.383,14.223-21.334a3.451,3.451,0,1,1,5.743,3.829l-16.928,25.393a3.453,3.453,0,0,1-2.776,1.536Z" transform="translate(-251.82 -178.764)" fill="#fff" />
                                </g>
                            </svg>

                            <DialogTitle>
                                <h2 class="text-base font-semibold text-center mb-2 mt-2">Kayıt Başarılı</h2>
                            </DialogTitle>
                            <p class="text-xs font-kiralabunuthin ts:text-sm text-center">Ürünü stoklarımıza eklendiğinde email ile haber verilecektir</p>

                            <div class="absolute top-2 right-5 cursor-pointer p-2" @click="closeInStockForm">x</div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
    <!-- End of the result popup -->
</template>
